/**
 * Normalize API values (epoch seconds, epoch ms, ISO strings) for display.
 * Gobodo / graph payloads often use numeric milliseconds; some sources use seconds.
 */

/** Ignore tiny magnitudes (often IDs, durations) — not plausible full Unix instants. */
const MIN_NUMERIC_EPOCH_MAGNITUDE = 1_000_000;

function toTimestampMs(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isFinite(t) && !Number.isNaN(t) ? t : null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    const a = Math.abs(value);
    if (a < MIN_NUMERIC_EPOCH_MAGNITUDE) return null;
    if (a < 1e11) return Math.round(value * 1000);
    return Math.round(value);
  }
  const s = String(value).trim();
  if (!s) return null;
  if (/^-?\d+\.?\d*$/.test(s)) {
    const n = Number(s);
    if (!Number.isFinite(n)) return null;
    const a = Math.abs(n);
    if (a < MIN_NUMERIC_EPOCH_MAGNITUDE) return null;
    if (a < 1e11) return Math.round(n * 1000);
    return Math.round(n);
  }
  const parsed = Date.parse(s);
  return Number.isFinite(parsed) ? parsed : null;
}

export function formatEpochOrDateAsUsDate(value: unknown): string {
  const ms = toTimestampMs(value);
  if (ms == null) {
    const fallback = typeof value === 'string' ? value : String(value ?? '');
    const d = new Date(fallback);
    return Number.isNaN(d.getTime()) ? fallback : d.toLocaleDateString('en-US');
  }
  return new Date(ms).toLocaleDateString('en-US');
}

export function formatEpochOrDateAsUsDateTime(value: unknown): string {
  const ms = toTimestampMs(value);
  if (ms == null) {
    const fallback = typeof value === 'string' ? value : String(value ?? '');
    const d = new Date(fallback);
    return Number.isNaN(d.getTime())
      ? fallback
      : d.toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        });
  }
  return new Date(ms).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/** True if value is likely an epoch instant (for default-type fields like text/number). */
export function looksLikeEpochInstant(value: unknown): boolean {
  return toTimestampMs(value) != null;
}
